import React from 'react'
import { Badge, Tag, Space, Typography } from 'antd'

const { Text } = Typography

// Format priority as a colored tag
export const getPriorityTag = (priority) => {
  let color
  switch (priority) {
    case 'Critical':
      color = 'red'
      break
    case 'High':
      color = 'orange'
      break
    case 'Medium':
      color = 'blue'
      break
    default:
      color = 'green'
  }
  return <Tag color={color}>{priority}</Tag>
}

// Get color for category
export const getCategoryColor = (category) => {
  switch (category) {
    case 'Meeting':
      return 'cyan'
    case 'Training':
      return 'purple'
    case 'Patient Care':
      return 'green'
    case 'Maintenance':
      return 'gold'
    case 'Administrative':
      return 'blue'
    case 'Schedule':
      return 'geekblue'
    case 'Certification':
      return 'magenta'
    case 'Documentation':
      return 'volcano'
    case 'Compliance':
      return 'orange'
    default:
      return 'default'
  }
}

// Format category as a colored tag
export const getCategoryTag = (category) => {
  return <Tag color={getCategoryColor(category)}>{category}</Tag>
}

// Get color for department
export const getDepartmentColor = (department) => {
  switch (department) {
    case 'Nursing':
      return 'blue'
    case 'Cardiology':
      return 'red'
    case 'Nephrology':
      return 'cyan'
    case 'Physical Therapy':
      return 'purple'
    case 'ICU':
      return 'volcano'
    case 'Oncology':
      return 'orange'
    case 'Endocrinology':
      return 'green'
    case 'All':
      return 'geekblue'
    default:
      return 'lime'
  }
}

// Format department as a colored tag
export const getDepartmentTag = (department) => {
  return <Tag color={getDepartmentColor(department)}>{department}</Tag>
}

// Format acknowledgement status as a badge
export const getAcknowledgedBadge = (isAcknowledged) => {
  return isAcknowledged ? <Badge status='success' text='Acknowledged' /> : <Badge status='warning' text='Pending' />
}

// Calculate and format time until reminder
export const getTimeUntil = (dateTimeString) => {
  const reminderDate = new Date(dateTimeString)
  const now = new Date()
  const diffTime = reminderDate - now

  if (diffTime < 0) return 'Past due'

  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

  if (diffDays > 0) {
    return `${diffDays}d ${diffHours}h`
  } else if (diffHours > 0) {
    return `${diffHours}h`
  } else {
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60))
    return `${diffMinutes}m`
  }
}

// Format date and time
export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-'
  return new Date(dateTimeString).toLocaleString()
}

// Format time until as a colored tag
export const formatTimeUntilTag = (dateTimeString) => {
  const timeUntil = getTimeUntil(dateTimeString)
  const isPastDue = timeUntil === 'Past due'
  const isUrgent = !isPastDue && new Date(dateTimeString) - new Date() < 24 * 60 * 60 * 1000

  return <Tag color={isPastDue ? 'red' : isUrgent ? 'orange' : 'green'}>{timeUntil}</Tag>
}

// Format friendly date display (Today, Tomorrow, etc.)
export const getFriendlyDateDisplay = (dateTimeString) => {
  const reminderDate = new Date(dateTimeString)
  const today = new Date()
  const isToday = reminderDate.toDateString() === today.toDateString()
  const isTomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000).toDateString() === reminderDate.toDateString()

  let dateDisplay
  if (isToday) {
    dateDisplay = `Today at ${reminderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  } else if (isTomorrow) {
    dateDisplay = `Tomorrow at ${reminderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  } else {
    dateDisplay =
      reminderDate.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ` at ${reminderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  return dateDisplay
}

// Format staff information with name and department
export const formatStaffInfo = (name, department) => (
  <Space direction='vertical' size={0}>
    <Text>{name}</Text>
    {getDepartmentTag(department)}
  </Space>
)

// Format reminder title with ID
export const formatReminderTitle = (title, id) => (
  <Space direction='vertical' size={0}>
    <Text strong>{title}</Text>
    <Text type='secondary' style={{ fontSize: '12px' }}>
      {id}
    </Text>
  </Space>
)
